import sysConf from 'config';
import { getAwsAuthToken } from 'utils/lib/authHelper';

class ApiClient {
    constructor() {
        this.localURL= "http://localhost:4000/";
        this.prodURL = sysConf.apiGateway.AI_BASE_NODE_URL;
        this.baseURL = this.prodURL;
        this.baseHeaders = {
            'Content-Type': 'application/json',
            'x-auth-source': sysConf.isDev ? 'test' : 'prod',
        };
    }

    async getAuthHeaders() {
        const token = await getAwsAuthToken();
        return {
            ...this.baseHeaders,
            'Authorization': `Bearer ${token}`,
        };
    }

    resolveUrl(url) {
        if (url.startsWith('http://') || url.startsWith('https://')) {
            return url;
        }
        const baseUrl = this.baseURL.endsWith('/') ? this.baseURL.slice(0, -1) : this.baseURL;
        const path = url.startsWith('/') ? url : `/${url}`;
        return `${baseUrl}${path}`;
    }

    buildUrl(url, params) {
        const resolvedUrl = this.resolveUrl(url);
        if (!params || Object.keys(params).length === 0) {
            return resolvedUrl;
        }
        const queryString = new URLSearchParams(params).toString();
        return `${resolvedUrl}${resolvedUrl.includes('?') ? '&' : '?'}${queryString}`;
    }

    async request(url, options = {}) {
        try {
            const { method = 'GET', body = null, params = null, headers = {} } = options;
            const authHeaders = await this.getAuthHeaders();
            const finalUrl = method === 'GET' && params ? this.buildUrl(url, params) : this.resolveUrl(url);
            
            const fetchOptions = {
                method,
                headers: {
                    ...authHeaders,
                    ...headers,
                },
            };

            if (body && method !== 'GET') {
                fetchOptions.body = JSON.stringify(body);
            }

            const response = await fetch(finalUrl, fetchOptions);

            if (!response.ok) {
                throw new Error(`API call failed: ${response.status} ${response.statusText}`);
            }

            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                return await response.json();
            }
            
            return await response.text();
        } catch(error) {
            console.error('API calling error:', error);
            throw error;
        }
    }

    async get(url, params = null, customHeaders = {}) {
        return this.request(url, {
            method: 'GET',
            params,
            headers: customHeaders,
        });
    }

    async post(url, body = null, customHeaders = {}) {
        return this.request(url, {
            method: 'POST',
            body,
            headers: customHeaders,
        });
    }

    async put(url, body = null, customHeaders = {}) {
        return this.request(url, {
            method: 'PUT',
            body,
            headers: customHeaders,
        });
    }

    async patch(url, body = null, customHeaders = {}) {
        return this.request(url, {
            method: 'PATCH',
            body,
            headers: customHeaders,
        });
    }

    async delete(url, customHeaders = {}) {
        return this.request(url, {
            method: 'DELETE',
            headers: customHeaders,
        });
    }
}

const apiClient = new ApiClient();

export const apiCalling = apiClient.request.bind(apiClient);
export const apiGet = apiClient.get.bind(apiClient);
export const apiPost = apiClient.post.bind(apiClient);
export const apiPut = apiClient.put.bind(apiClient);
export const apiPatch = apiClient.patch.bind(apiClient);
export const apiDelete = apiClient.delete.bind(apiClient);

export default apiClient;